import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';

import { AppComponent } from './app.component';
import { SideNavOuterToolbarModule, SingleCardModule } from './layouts';
import { AppFooterModule } from 'src/app/components/library/app-footer/app-footer.component';
import { UnexpectedErrorDialogComponent } from './components/unexpected-error-dialog/unexpected-error-dialog.component';
import { AppUpdatePromptComponent } from './components/app-update-prompt/app-update-prompt.component';
import { AppUpdateService } from './services/app-update/app-update.service';
import { isChunkLoadError } from './services/app-update/chunk-load-error';

import {
  MsalModule,
  MsalRedirectComponent,
  MsalGuard,
  MsalInterceptor,
  MSAL_INSTANCE,
} from '@azure/msal-angular';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';

import { ScreenService, AppInfoService } from './services';
import { UnauthenticatedContentModule } from './layouts/unauthenticated-content/unauthenticated-content';
import { AppRoutingModule } from './app-routing.module';
import { CalystaAPIInterceptor } from './interceptors/Calysta-api-interceptor';
import { ThemeService } from './services/theme.service';
import devextremeAjax from 'devextreme/core/utils/ajax';
import { sendRequestFactory } from './ng-http-client-helper';
import { AuthService } from './services/auth/auth.service';
import { Router } from '@angular/router';
import { LocationStrategy, HashLocationStrategy, DatePipe } from '@angular/common';
import config from 'devextreme/core/config';
import { environment } from 'src/environments/environment';
import * as Sentry from '@sentry/angular';
import { ErrorReportingService } from './services/error-reporting/error-reporting.service';
import { UnexpectedErrorDialogService } from './services/error-reporting/unexpected-error-dialog.service';


@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent, MsalRedirectComponent],
    imports: [BrowserModule,
        SideNavOuterToolbarModule,
        SingleCardModule,
        AppFooterModule,
        UnauthenticatedContentModule,
        AppRoutingModule,
        UnexpectedErrorDialogComponent,
        AppUpdatePromptComponent,
        MsalModule.forRoot(new PublicClientApplication({
            auth: {
                clientId: 'c45302dd-2ae4-48c1-984b-ba3b5284ec97',
                authority: 'https://login.microsoftonline.com/7cb14eb4-fad0-494b-9cf4-45fa0d69fb59',
                redirectUri: window.location.origin,
            },
            cache: {
                cacheLocation: 'localStorage',
            },
        }), {
            interactionType: InteractionType.Redirect, // MSAL Guard Configuration.
            authRequest: {
                scopes: ['user.read'],
            },
        }, {
            interactionType: InteractionType.Redirect,
            protectedResourceMap: new Map([
                ['https://graph.microsoft.com', ['user.read']],
            ]),
        }),
        ], providers: [
        AuthService,
        ScreenService,
        AppInfoService,
        MsalGuard,
        DatePipe,
        ThemeService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CalystaAPIInterceptor,
            multi: true,
        },
        {
            // Sentry's handler captures the exception; wrapped so a genuinely uncaught JS error
            // (a template binding, an unhandled promise) also shows the persistent dialog, the
            // same one HTTP failures use, with an error_reference the user can quote. Checks ErrorReportingService's tag first: an HTTP
            // failure the interceptor already reported and dialogued must not be reported and
            // dialogued a second time if it also reaches this handler. Also skips errors
            // isExpected() would classify as expected validation failures -- a subscribe() with
            // no error callback lets those reach here untagged (nothing tags an expected error),
            // and they must still produce no Sentry event and no dialog.
            provide: ErrorHandler,
            useFactory: (
              reporting: ErrorReportingService,
              dialog: UnexpectedErrorDialogService,
              appUpdate: AppUpdateService,
            ) => {
              const sentryHandler = Sentry.createErrorHandler();
              return {
                handleError: (error: unknown) => {
                  // Stale tab after a deploy (lazy chunk gone, also from router navigations): the
                  // fix is a reload, not a bug report -- no Sentry event, no generic dialog.
                  if (isChunkLoadError(error)) {
                    appUpdate.show();
                    return;
                  }
                  if (reporting.wasReported(error) || reporting.isExpected(error)) {
                    return;
                  }
                  dialog.show(reporting.reportUncaught(error, sentryHandler));
                },
              };
            },
            deps: [ErrorReportingService, UnexpectedErrorDialogService, AppUpdateService],
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (msalInstance: IPublicClientApplication) => () => msalInstance.initialize(),
            deps: [MSAL_INSTANCE],
            multi: true,
        },
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy,
        },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
    ] })

export class AppModule {
  constructor(httpClient: HttpClient) {
    devextremeAjax.inject({ sendRequest: sendRequestFactory(httpClient) });
  }
}
